import useAudios from 'hooks/useAudios';
import useQueues from 'hooks/useQueues';
import useSatisfactionSurvey from 'hooks/useSatisfactionSurvey';
import useSurveyTags from 'hooks/useSurveyTags';
import React, { useCallback, useEffect, useState } from 'react';
import Loader from 'react-loader-spinner';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Container, Row } from 'reactstrap';
import ConfigSurveyFields from './ConfigSurveyFields';

const NPS_LABELS = [
  'Péssimo',
  'Muito Ruim',
  'Ruim',
  'Insuficiente',
  'Mediano',
  'Suficiente',
  'Satisfatório',
  'Bom',
  'Muito bom',
  'Ótimo'
];

const findOption = (value, options) => {
  if (value === null || value === undefined) return null;
  const searchValue = String(value);
  return options.find((opt) => String(opt.value) === searchValue) || null;
};

const findOptions = (values, options) => {
  if (!Array.isArray(values) || values.length === 0) return [];
  const searchValues = new Set(
    values.filter((v) => v !== null && v !== undefined).map((v) => String(v))
  );
  if (searchValues.size === 0) return [];
  return options.filter((opt) => searchValues.has(String(opt.value)));
};

const TYPE_OPTIONS = [
  { label: 'CSAT', value: 'CSAT' },
  { label: 'NPS', value: 'NPS' },
  { label: 'Tags', value: 'TAG' },
  { label: 'Texto Livre', value: 'FREE_TEXT' }
];

const mapSurveyDataToForm = (
  data = {},
  queueOptions = [],
  uraAudioOptions = [],
  finalAudioOptions = [],
  tagOptions = []
) => {
  const selectedQueueOptions = findOptions(
    data.queues?.map?.((q) => q.id),
    queueOptions
  );
  const selectedInitialAudio = findOption(data.initialAudioId, uraAudioOptions);
  const selectedFinalAudio = findOption(data.finalAudioId, finalAudioOptions);

  const mappedQuestions = (data.questions ?? []).map((question, qIndex) => {
    const mappedAudioId = findOption(question.audioId, uraAudioOptions);
    const mappedType = findOption(question.type, TYPE_OPTIONS);

    const mappedOptions = (question.options ?? []).map((opt, optIndex) => {
      let resolvedTagId = null;

      if (question.type === 'TAG') {
        if (opt.tag) {
          resolvedTagId = opt.tag?.id ?? opt.tag;
        } else if (opt.label && tagOptions.length > 0) {
          const foundTag = tagOptions.find((t) => t.label === opt.label);
          if (foundTag) {
            resolvedTagId = foundTag.value;
          }
        }
      }

      return {
        id: opt.id,
        tag: resolvedTagId,
        rate: opt.rate,
        label: opt.label,
        order: opt.order
      };
    });

    const mappedQuestion = {
      id: question.id,
      name: question.name,
      message: question.message,
      order: question.order,
      isMain: question.isMain ?? false,
      audioId: mappedAudioId || question.audioId,
      type: mappedType || question.type,
      options: mappedOptions,
      messageListConfig: question.messageListConfig || {
        title: null,
        button: null,
        body: null,
        caption: null
      },
      messageButtonConfig: question.messageButtonConfig || {
        title: null,
        button: null,
        body: null,
        caption: null
      }
    };

    return mappedQuestion;
  });

  return {
    name: data.name || '',
    queues: selectedQueueOptions,
    initialMessage: data.initialMessage || '',
    finalMessage: data.finalMessage || '',
    quantityOfInvalidAttempts: data.quantityOfInvalidAttempts ?? 2,
    incorrectOptionMessage: data.incorrectOptionMessage || 'Opção inválida.',
    initialAudioId: selectedInitialAudio,
    finalAudioId: selectedFinalAudio,
    sendToActiveContact: data.sendToActiveContact ?? false,
    reminderToResend: data.reminderToResend ?? false,
    reminderAttempts: data.reminderAttempts || 0,
    reminderTime: data.reminderTime || 0,
    reminderMessage: data.reminderMessage || '',
    questions: mappedQuestions
  };
};

const defaultValues = {
  active: true,
  name: '',
  initialMessage: '',
  finalMessage: '',
  sendToActiveContact: false,
  quantityOfInvalidAttempts: 0,
  incorrectOptionMessage: '',
  queues: [],
  initialAudioId: null,
  finalAudioId: null,
  reminderToResend: false,
  reminderAttempts: 0,
  reminderTime: 0,
  reminderMessage: '',
  questions: []
};

const ConfigSurveyForm = (props) => {
  const { id } = props.match.params;
  const history = useHistory();
  const isUpdate = !!id;

  const [generalConfigs, setGeneralConfigs] = useState();
  const [formData, setFormData] = useState(defaultValues);
  const [isDirty, setIsDirty] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const {
    isLoading: isLoadingSatisfactionSurveys,
    getSurveyById,
    fetchGeneralConfigs,
    createSurvey,
    updateSurvey,
    schemas: { formResolverSchema }
  } = useSatisfactionSurvey();

  const { options: queueOptions, isLoading: isLoadingQueues } = useQueues();

  const {
    groupedByTypeOptions: {
      ura: questionAudioOptions,
      sound: finalAudioOptions
    },
    isLoading: isLoadingAudios
  } = useAudios();

  const {
    options: tagOptions,
    getAllTags,
    isLoading: isLoadingTags
  } = useSurveyTags();

  useEffect(() => {
    getAllTags();
  }, [getAllTags]);

  useEffect(() => {
    fetchGeneralConfigs().then((res) => {
      const configData = res?.data || res;
      setGeneralConfigs(configData);
    });
  }, [fetchGeneralConfigs]);

  // Carrega dados da API e inicializa o formData
  useEffect(() => {
    const initData = async () => {
      if (!id) {
        setFormData(
          mapSurveyDataToForm(
            defaultValues,
            queueOptions,
            questionAudioOptions,
            finalAudioOptions,
            tagOptions
          )
        );
        setIsDataLoaded(true);
        return;
      }

      try {
        const res = await getSurveyById(id);
        const dataToMap = res || defaultValues;
        const mapped = mapSurveyDataToForm(
          dataToMap,
          queueOptions,
          questionAudioOptions,
          finalAudioOptions,
          tagOptions
        );
        setFormData(mapped);
      } catch (err) {
        console.error('[ConfigSurveyForm] Erro ao exibir pesquisa:', err);
        setFormData(
          mapSurveyDataToForm(
            defaultValues,
            queueOptions,
            questionAudioOptions,
            finalAudioOptions,
            tagOptions
          )
        );
      } finally {
        setIsDataLoaded(true);
      }
    };

    if (!isLoadingQueues && !isLoadingAudios && !isLoadingTags) {
      initData();
    }
  }, [
    id,
    getSurveyById,
    isLoadingQueues,
    isLoadingAudios,
    isLoadingTags,
    queueOptions,
    questionAudioOptions,
    finalAudioOptions,
    tagOptions
  ]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  const handleQuestionsChange = useCallback((newQuestions) => {
    setFormData((prev) => ({ ...prev, questions: newQuestions }));
    setIsDirty(true);
  }, []);

  const handleAddQuestion = useCallback(() => {
    const currentQuestions = formData.questions ?? [];
    const isFirstQuestion = currentQuestions.length === 0;
    const isMain = isFirstQuestion;

    let initialType = { label: 'Texto Livre', value: 'FREE_TEXT' };
    let initialOptions = [];

    if (isMain) {
      initialType = { label: 'NPS', value: 'NPS' };
      initialOptions = Array.from({ length: 10 }, (_, i) => ({
        rate: i + 1,
        label: NPS_LABELS[i],
        tag: null,
        id: null
      }));
    } else {
      initialType = { label: 'Texto Livre', value: 'FREE_TEXT' };
      initialOptions = [];
    }

    const newQuestion = {
      id: `temp-${Date.now()}`,
      name: `Pergunta ${currentQuestions.length + 1}`,
      audioId: null,
      message: '',
      type: initialType,
      order: currentQuestions.length + 1,
      isMain: isMain,
      options: initialOptions,
      messageListConfig: {
        title: null,
        button: null,
        body: null,
        caption: null
      },
      messageButtonConfig: {
        title: null,
        button: null,
        body: null,
        caption: null
      }
    };

    handleQuestionsChange([...currentQuestions, newQuestion]);
  }, [formData.questions, handleQuestionsChange]);

  const handleRemoveQuestion = useCallback(
    (indexToRemove) => {
      const updatedQuestions = formData.questions.filter(
        (_, index) => index !== indexToRemove
      );
      handleQuestionsChange(updatedQuestions);
    },
    [formData.questions, handleQuestionsChange]
  );

  const handleUpdateQuestion = (index, field, value) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    handleQuestionsChange(updatedQuestions);
  };

  const formatDataForSubmit = useCallback(
    (currentData) => {
      const dataToSubmit = {
        name: currentData.name,
        active: true,
        queues: (currentData.queues ?? []).map((q) => ({
          id: q.value,
          name: q.label
        })),
        initialMessage: currentData.initialMessage,
        finalMessage: currentData.finalMessage,
        initialAudioId:
          currentData.initialAudioId?.value ||
          currentData.initialAudioId ||
          null,
        finalAudioId:
          currentData.finalAudioId?.value || currentData.finalAudioId || null,
        sendToActiveContact: currentData.sendToActiveContact,
        quantityOfInvalidAttempts: Number(
          currentData.quantityOfInvalidAttempts
        ),
        incorrectOptionMessage: currentData.incorrectOptionMessage,
        reminderToResend: currentData.reminderToResend,
        questions: (currentData.questions ?? []).map((q, index) => {
          const typeValue = q.type?.value || q.type;
          const isTagType = typeValue === 'TAG';

          const question = {
            name: q.name,
            message: q.message,
            audioId: q.audioId?.value || q.audioId || null,
            type: typeValue,
            isMain: q.isMain ?? false,
            order: index + 1,
            messageListConfig: q.messageListConfig,
            messageButtonConfig: q.messageButtonConfig,
            options: (q.options ?? []).map((opt) => {
              let optionData = {
                rate:
                  opt.rate !== undefined && opt.rate !== null
                    ? opt.rate
                    : undefined,
                order: opt.order
              };

              if (isTagType) {
                const selectedTagOption = tagOptions?.find(
                  (t) => String(t.value) === String(opt.tag)
                );
                if (opt.tag) optionData.tag = opt.tag;
                if (selectedTagOption)
                  optionData.label = selectedTagOption.label;
              } else {
                if (opt.id) optionData.id = opt.id;
                if (opt.label) optionData.label = opt.label;
              }
              return optionData;
            })
          };

          if (
            isUpdate &&
            q.id &&
            typeof q.id === 'string' &&
            !q.id.includes('-')
          ) {
            question.id = q.id;
          }
          return question;
        })
      };

      if (currentData.reminderToResend) {
        dataToSubmit.reminderAttempts = Number(currentData.reminderAttempts);
        dataToSubmit.reminderTime = Number(currentData.reminderTime);
        dataToSubmit.reminderMessage = currentData.reminderMessage;
      }
      return dataToSubmit;
    },
    [isUpdate, tagOptions]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const validation = formResolverSchema.safeValidateSync(formData, {
        abortEarly: false
      });
      if (!validation.success) {
        validation.error.errors.forEach((err) => toast.error(err));
        return;
      }

      const questions = formData.questions || [];
      const questionNames = questions.map((q) => (q.name || '').trim());
      const uniqueNames = new Set(questionNames);

      if (questionNames.length !== uniqueNames.size) {
        toast.error('Não é permitido cadastrar perguntas com nomes idênticos.');
        return;
      }

      const hasEmptyTags = questions.some((q) => {
        const typeValue = q.type?.value || q.type;
        if (typeValue === 'TAG') {
          return (q.options || []).some(
            (opt) => !opt.tag || opt.tag === null || opt.tag === ''
          );
        }
        return false;
      });

      if (hasEmptyTags) {
        toast.error(
          'Existem perguntas do tipo TAG com opções sem tag selecionada. Por favor, preencha todas as tags.'
        );
        return;
      }

      const duplicateTagQuestion = questions.find((q) => {
        const typeValue = q.type?.value || q.type;
        if (typeValue === 'TAG') {
          const tags = (q.options || [])
            .map((opt) => opt.tag?.id ?? opt.tag)
            .filter((t) => t !== null && t !== undefined && t !== '')
            .map((t) => String(t));
          const uniqueTags = new Set(tags);
          return tags.length !== uniqueTags.size;
        }
        return false;
      });

      if (duplicateTagQuestion) {
        toast.error(
          `A pergunta "${duplicateTagQuestion.name}" possui opções com a mesma TAG selecionada. Não é permitido repetir tags na mesma pergunta.`
        );
        return;
      }

      try {
        const dataToSubmit = formatDataForSubmit(formData);
        if (isUpdate) {
          await updateSurvey(id, dataToSubmit);
        } else {
          await createSurvey(dataToSubmit);
        }
        setIsDirty(false);
        history.push('/admin/configSurvey/list');
      } catch (err) {
        console.error('Erro no submit:', err);
        const errorMessage =
          err.response?.data?.message ||
          'Ocorreu um erro ao salvar a pesquisa.';
        toast.error(errorMessage);
      }
    },
    [
      formData,
      formResolverSchema,
      formatDataForSubmit,
      id,
      isUpdate,
      createSurvey,
      updateSurvey,
      history
    ]
  );

  const isLoading =
    !isDataLoaded ||
    isLoadingAudios ||
    isLoadingQueues ||
    isLoadingSatisfactionSurveys ||
    isLoadingTags;

  if (isLoading) {
    return (
      <Container className="mt-6" fluid>
        <Row className="justify-content-md-center">
          <Loader type="TailSpin" color="#35ac50" height={100} width={100} />
        </Row>
      </Container>
    );
  }

  return (
    <ConfigSurveyFields
      isUpdate={isUpdate}
      isDirty={isDirty}
      isLoading={isLoading}
      formData={formData}
      generalConfigs={generalConfigs}
      queueOptions={queueOptions}
      questionAudioOptions={questionAudioOptions}
      finalAudioOptions={finalAudioOptions}
      tagOptions={tagOptions}
      handleChange={handleChange}
      handleQuestionsChange={handleQuestionsChange}
      handleAddQuestion={handleAddQuestion}
      handleRemoveQuestion={handleRemoveQuestion}
      handleUpdateQuestion={handleUpdateQuestion}
      onSubmit={onSubmit}
    />
  );
};

export default ConfigSurveyForm;
